import { useContext, useState, useRef } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Skeleton,
  Alert,
  CircularProgress,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColorModeContext } from '../../../contexts/ColorModeContext';
import { palette, typography, theme as themeColors } from '../../../theme/colors';
import { useVariants } from '../../../hooks/useVariants';
import { useSnackbar } from '../../../components/SnackbarProvider';
import axiosInstance from '../../../lib/axios';
import type { ProductImage, ProductVariant } from '../../../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface VariantImagesTabProps {
  productId: string;
}

interface SortableImageProps {
  image: ProductImage;
  index: number;
  colors: any;
  deleting: string | null;
  onDelete: (image: ProductImage) => void;
  getImageUrl: (image: ProductImage) => string;
}

const SortableImage = ({
  image,
  index,
  colors,
  deleting,
  onDelete,
  getImageUrl,
}: SortableImageProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        position: 'relative',
        aspectRatio: '1',
        borderRadius: themeColors.radius.lg,
        overflow: 'hidden',
        border: `1px solid ${isDragging ? palette.primary : colors.border}`,
        backgroundColor: colors.bgElevated,
      }}
    >
      <Box
        component="img"
        src={getImageUrl(image)}
        alt={image.originalName || `Variant image ${index + 1}`}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
        onError={(e: any) => {
          e.target.style.display = 'none';
        }}
      />

      {/* Drag handle */}
      <IconButton
        {...attributes}
        {...listeners}
        size="small"
        sx={{
          position: 'absolute',
          top: 4,
          left: 4,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          color: '#fff',
          cursor: 'grab',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
      </IconButton>

      {/* Delete button */}
      <IconButton
        size="small"
        onClick={() => onDelete(image)}
        disabled={deleting === image.id}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          color: '#fff',
          '&:hover': {
            backgroundColor: palette.error,
          },
        }}
      >
        {deleting === image.id ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>

      {/* Image number badge */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 4,
          left: 4,
          backgroundColor: index === 0 ? palette.primary : 'rgba(0, 0, 0, 0.6)',
          color: '#fff',
          px: 1,
          py: 0.25,
          borderRadius: themeColors.radius.sm,
          fontSize: '10px',
          fontWeight: typography.fontWeight.semibold,
        }}
      >
        {index === 0 ? 'Main' : `#${index + 1}`}
      </Box>
    </Box>
  );
};

interface VariantImageGalleryProps {
  variant: ProductVariant;
  productId: string;
  colors: any;
}

const VariantImageGallery = ({ variant, productId, colors }: VariantImageGalleryProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localImages, setLocalImages] = useState<ProductImage[]>(variant.images || []);
  const { showMessage } = useSnackbar();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getImageUrl = (image: ProductImage) => {
    const path = image.medium || image.thumbnail || image.large;
    if (!path) return '';
    if (path.startsWith('http')) {
      return path;
    }
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `${API_URL}/${cleanPath}`;
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axiosInstance.post(
        `/products/${productId}/variants/${variant.id}/images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setLocalImages(response.data.images || []);
      showMessage('Image uploaded successfully', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to upload image';
      setError(errorMessage);
      showMessage(errorMessage, 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async (image: ProductImage) => {
    setDeleting(image.id);
    setError(null);

    try {
      const response = await axiosInstance.delete(
        `/products/${productId}/variants/${variant.id}/images/${image.id}`
      );
      setLocalImages(response.data.images || []);
      showMessage('Image deleted successfully', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to delete image';
      setError(errorMessage);
      showMessage(errorMessage, 'error');
    } finally {
      setDeleting(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = localImages.findIndex((img) => img.id === active.id);
    const newIndex = localImages.findIndex((img) => img.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newImages = arrayMove(localImages, oldIndex, newIndex);
    setLocalImages(newImages);

    setReordering(true);
    setError(null);

    try {
      const imageIds = newImages.map((img) => img.id);
      await axiosInstance.patch(
        `/products/${productId}/variants/${variant.id}/images/reorder`,
        { imageIds }
      );
      showMessage('Images reordered successfully', 'success');
    } catch (err: any) {
      setLocalImages(localImages);
      const errorMessage = err.response?.data?.message || 'Failed to reorder images';
      setError(errorMessage);
      showMessage(errorMessage, 'error');
    } finally {
      setReordering(false);
    }
  };

  const formatAttributes = (attributes: Record<string, unknown>): string => {
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: typography.fontWeight.semibold,
            color: colors.text,
          }}
        >
          {variant.sku} - {formatAttributes(variant.attributes)}
        </Typography>
        {reordering && (
          <CircularProgress size={14} sx={{ color: palette.primary }} />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={localImages.map((img) => img.id)} strategy={rectSortingStrategy}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 2,
            }}
          >
            {localImages.map((image, index) => (
              <SortableImage
                key={image.id}
                image={image}
                index={index}
                colors={colors}
                deleting={deleting}
                onDelete={handleDeleteImage}
                getImageUrl={getImageUrl}
              />
            ))}

            {/* Upload Button */}
            <Box
              onClick={handleUploadClick}
              sx={{
                aspectRatio: '1',
                borderRadius: themeColors.radius.lg,
                border: `2px dashed ${colors.border}`,
                backgroundColor: colors.bg,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                transition: themeColors.transitions.fast,
                '&:hover': uploading ? {} : {
                  borderColor: palette.primary,
                  backgroundColor: colors.bgHover,
                },
              }}
            >
              {uploading ? (
                <CircularProgress size={24} sx={{ color: palette.primary }} />
              ) : (
                <>
                  <AddPhotoAlternateOutlinedIcon
                    sx={{ color: colors.textMuted, fontSize: 28, mb: 0.5 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontSize: '10px',
                      textAlign: 'center',
                    }}
                  >
                    Add Image
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </SortableContext>
      </DndContext>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {localImages.length === 0 && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1.5,
            color: colors.textMuted,
            fontSize: '11px',
          }}
        >
          No images uploaded for this variant yet. Click "Add Image" to upload.
        </Typography>
      )}
    </Box>
  );
};

export const VariantImagesTab = ({ productId }: VariantImagesTabProps) => {
  const { mode } = useContext(ColorModeContext);
  const { data: variants = [], isLoading, error } = useVariants(productId);
  const [expandedVariant, setExpandedVariant] = useState<string | false>(false);

  const colors = mode === 'dark' ? {
    bg: palette.surface,
    bgElevated: palette.surfaceElevated,
    bgHover: palette.surfaceHover,
    border: palette.border,
    borderLight: palette.borderLight,
    text: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.surface,
    bgElevated: palette.light.surfaceElevated,
    bgHover: palette.light.surfaceHover,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    text: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const handleAccordionChange = (variantId: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedVariant(isExpanded ? variantId : false);
  };

  const formatAttributes = (attributes: Record<string, unknown>): string => {
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  const getTotalImages = () => {
    return variants.reduce((sum, variant) => sum + (variant.images?.length || 0), 0);
  };

  return (
    <Card
      sx={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: themeColors.radius.md,
      }}
    >
      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CameraAltIcon sx={{ color: palette.primary, fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.text,
                textTransform: 'uppercase',
              }}
            >
              Variant Images
            </Typography>
          </Box>
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={60}
                sx={{
                  mb: 1,
                  borderRadius: themeColors.radius.sm,
                  backgroundColor: colors.bgElevated,
                }}
              />
            ))}
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert
            severity="error"
            sx={{
              borderRadius: themeColors.radius.sm,
              fontFamily: typography.fontFamily.body,
            }}
          >
            Failed to load variants. Please try again.
          </Alert>
        )}

        {/* Empty State */}
        {!isLoading && !error && variants.length === 0 && (
          <Alert
            severity="info"
            sx={{
              borderRadius: themeColors.radius.sm,
              fontFamily: typography.fontFamily.body,
            }}
          >
            No variants found. Create variants first to upload variant-specific images.
          </Alert>
        )}

        {/* Variants List */}
        {!isLoading && !error && variants.length > 0 && (
          <>
            <Box sx={{ mb: 3 }}>
              {variants.map((variant) => (
                <Accordion
                  key={variant.id}
                  expanded={expandedVariant === variant.id}
                  onChange={handleAccordionChange(variant.id)}
                  sx={{
                    backgroundColor: colors.bgElevated,
                    border: `1px solid ${colors.border}`,
                    borderRadius: `${themeColors.radius.sm} !important`,
                    mb: 1,
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      margin: '0 0 8px 0',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: colors.text }} />}
                    sx={{
                      backgroundColor: colors.bgElevated,
                      borderRadius: themeColors.radius.sm,
                      '&:hover': {
                        backgroundColor: colors.bgHover,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Typography
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontWeight: typography.fontWeight.semibold,
                          color: colors.text,
                          fontSize: typography.fontSize.sm,
                        }}
                      >
                        {variant.sku}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: typography.fontFamily.body,
                          color: colors.textSecondary,
                          fontSize: typography.fontSize.sm,
                          flex: 1,
                        }}
                      >
                        {formatAttributes(variant.attributes)}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          color: colors.textMuted,
                          fontSize: typography.fontSize.xs,
                          backgroundColor: alpha(palette.primary, 0.1),
                          px: 1.5,
                          py: 0.5,
                          borderRadius: themeColors.radius.sm,
                        }}
                      >
                        {variant.images?.length || 0} {variant.images?.length === 1 ? 'image' : 'images'}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      backgroundColor: colors.bg,
                      borderTop: `1px solid ${colors.border}`,
                      p: 3,
                    }}
                  >
                    <VariantImageGallery
                      variant={variant}
                      productId={productId}
                      colors={colors}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {/* Summary */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: themeColors.radius.sm,
                backgroundColor: colors.bgElevated,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                  letterSpacing: typography.letterSpacing.widest,
                  color: colors.textMuted,
                }}
              >
                Summary: {variants.length} variant{variants.length !== 1 ? 's' : ''} • {getTotalImages()} total image{getTotalImages() !== 1 ? 's' : ''}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1,
                  fontFamily: typography.fontFamily.body,
                  fontSize: typography.fontSize.xs,
                  color: colors.textMuted,
                }}
              >
                Drag images to reorder within each variant. First image is the main variant image. Supported: JPG, PNG, GIF, WebP (max 5MB).
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};
